/**
 * src/shell/portal/components/LiveStreamRoom.tsx
 *
 * Live WebRTC Broadcasting & Private Watch Party Overlay.
 * - Hardware stream controls: Camera, Mic, Display capture (`getDisplayMedia`).
 * - Dual Modes: Public Channel Stream vs Encrypted Watch Party.
 * - Live Pinned Garage Shop Product with 1-Click Buy via Escrow.
 */

import React, { useState, useEffect, useRef } from 'react';
import { webrtcBroadcaster, BroadcastSession } from '../../../mesh/broadcast/webrtc-broadcaster';
import { GarageProduct } from '../../../plugins/commerce/GarageShopDrawer';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  Radio,
  X,
  Users,
  ShoppingBag,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';

export interface LiveStreamRoomProps {
  session: BroadcastSession;
  onClose: () => void;
  pinnedProduct?: GarageProduct;
  onBuyProduct?: (product: GarageProduct) => void;
}

export const LiveStreamRoom: React.FC<LiveStreamRoomProps> = ({
  session,
  onClose,
  pinnedProduct,
  onBuyProduct,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [viewerCount, setViewerCount] = useState(session.viewerCount || 1);

  useEffect(() => {
    const stream = webrtcBroadcaster.getActiveStream();
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }

    // Simulate viewers discovering stream and update relay topology
    const interval = setInterval(() => {
      setViewerCount((v) => {
        const next = v + Math.floor(Math.random() * 3);
        try {
          webrtcBroadcaster.setViewerCount(next);
        } catch {}
        return next;
      });
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const handleToggleMute = () => {
    const active = webrtcBroadcaster.toggleMute();
    setIsMuted(!active);
  };

  const handleToggleCamera = () => {
    const active = webrtcBroadcaster.toggleCamera();
    setIsCameraOff(!active);
  };

  const handleStop = () => {
    webrtcBroadcaster.stopBroadcast();
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 24,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 960,
          background: '#0c101a',
          border: '1px solid #1e293b',
          borderRadius: 20,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8)',
        }}
      >
        {/* Stream Top Bar */}
        <div
          style={{
            padding: '14px 20px',
            background: '#111726',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                background: '#ef4444',
                color: '#ffffff',
                padding: '3px 8px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                animation: 'pulse 2s infinite',
              }}
            >
              <Radio size={12} /> LIVE
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#f8fafc' }}>{session.title}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                {session.privacy === 'private_watch_party' ? '🔒 Private P2P Watch Party' : '🌐 Public Channel Broadcast'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                fontSize: 11,
                padding: '3px 8px',
                borderRadius: 6,
                fontWeight: 700,
                background: viewerCount > 8 ? 'rgba(56, 189, 248, 0.15)' : 'rgba(52, 211, 153, 0.15)',
                border: `1px solid ${viewerCount > 8 ? '#38bdf8' : '#34d399'}`,
                color: viewerCount > 8 ? '#38bdf8' : '#34d399',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <span style={{ fontSize: 8 }}>●</span>
              {viewerCount > 8 ? 'Mediasoup SFU (1x Uplink)' : 'P2P Mesh (Direct)'}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#38bdf8', fontSize: 12, fontWeight: 700 }}>
              <Users size={14} /> {viewerCount.toLocaleString()} watching
            </div>
            <button
              type="button"
              onClick={handleStop}
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid #ef4444',
                color: '#ef4444',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              End Stream
            </button>
          </div>
        </div>

        {/* Video Canvas Area */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 480,
            background: '#06080e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />

          {/* Placeholder if camera track is off */}
          {isCameraOff && (
            <div style={{ position: 'absolute', color: '#64748b', fontSize: 14, fontWeight: 600 }}>
              Camera Video Off
            </div>
          )}

          {/* Live Pinned Product Card in Stream Overlay */}
          {pinnedProduct && (
            <div
              style={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                background: 'rgba(15, 23, 42, 0.92)',
                backdropFilter: 'blur(10px)',
                border: '1px solid #0284c7',
                borderRadius: 12,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                maxWidth: 360,
              }}
            >
              <div style={{ fontSize: 24 }}>{pinnedProduct.previewMedia.thumbnailUrl}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#38bdf8', fontWeight: 700, textTransform: 'uppercase' }}>
                  FEATURED IN STREAM
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#f8fafc' }}>{pinnedProduct.title}</div>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#10b981' }}>
                  {pinnedProduct.price.currency === 'USD' ? `$${pinnedProduct.price.amount}` : `${pinnedProduct.price.amount} TOKENS`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onBuyProduct && onBuyProduct(pinnedProduct)}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 12px',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  whiteSpace: 'nowrap',
                }}
              >
                <ShieldCheck size={14} /> Buy via Escrow
              </button>
            </div>
          )}
        </div>

        {/* Stream Control Bar */}
        <div
          style={{
            padding: '14px 20px',
            background: '#111726',
            borderTop: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
          }}
        >
          <button
            type="button"
            onClick={handleToggleMute}
            style={{
              background: isMuted ? '#ef4444' : '#1e293b',
              border: 'none',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              cursor: 'pointer',
            }}
          >
            {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
          </button>

          <button
            type="button"
            onClick={handleToggleCamera}
            style={{
              background: isCameraOff ? '#ef4444' : '#1e293b',
              border: 'none',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              cursor: 'pointer',
            }}
          >
            {isCameraOff ? <VideoOff size={18} /> : <Video size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};
